import React, { useEffect, useState } from 'react';
import securityConfig from '../../config/security';

const SecurityWrapper = ({ children }) => {
  const [isSecure, setIsSecure] = useState(true);

  useEffect(() => {
    // Check if development mode is enabled
    if (securityConfig.developmentMode) {
      console.log('Security disabled in development mode');
      return;
    }

    // Mobile detection to prevent false positives
    const isMobile = () => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
             window.innerWidth <= 768 ||
             ('ontouchstart' in window) ||
             (navigator.maxTouchPoints > 0);
    };

    // If mobile device, disable aggressive checks
    if (isMobile()) {
      console.log('Mobile device detected - reducing security checks');
      // Only do basic protection on mobile
      const preventBasicDebugging = (e) => {
        if (e.key === 'F12') {
          e.preventDefault();
          return false;
        }
      };
      document.addEventListener('keydown', preventBasicDebugging);
      return () => {
        document.removeEventListener('keydown', preventBasicDebugging);
      };
    }

    // Immediate check for DevTools already open at page load
    const immediateDevToolsCheck = () => {
      // Check if DevTools is already open when page loads
      const isAlreadyOpen = () => {
        // Method 1: Window size difference (immediate)
        const widthDiff = window.outerWidth - window.innerWidth;
        const heightDiff = window.outerHeight - window.innerHeight;
        
        // More lenient for immediate check but still detect obvious DevTools
        if ((widthDiff > 200 || heightDiff > 200) && !document.fullscreenElement) {
          return true;
        }
        
        // Method 2: Check for DevTools specific properties
        if (window.devtools && window.devtools.open) {
          return true;
        }
        
                 // Method 3: Console detection trick
         let devtools = false;
         const element = new Image();
         Object.defineProperty(element, 'id', {
           get: function() {
             devtools = true;
           }
         });
         console.log('%c', element);
         console.clear();
         
         // Method 4: Check for debugger timing (immediate)
         const start = performance.now();
         debugger;
         const debuggerTime = performance.now() - start;
         if (debuggerTime > 100) {
           devtools = true;
         }
         
         return devtools;
      };
      
      if (isAlreadyOpen()) {
        console.log('DevTools detected at page load');
        setIsSecure(false);
        blockAccess();
        return true;
      }
      return false;
    };

    // Enhanced security checks with better accuracy (DESKTOP ONLY)
    const performSecurityChecks = () => {
      // Check for clear signs of DevTools being open
      const devToolsChecks = [
        () => window.devtools && window.devtools.open,
        // More accurate window size detection - DESKTOP ONLY
        () => {
          const widthDiff = window.outerWidth - window.innerWidth;
          const heightDiff = window.outerHeight - window.innerHeight;
          // Only trigger if significant difference AND not in normal browser chrome range AND not mobile
          return (widthDiff > 300 || heightDiff > 300) && !document.fullscreenElement && !isMobile();
        },
        () => navigator.userAgent.includes('HeadlessChrome'),
        () => window.navigator.webdriver === true,
        () => window.callPhantom || window._phantom,
        // Check for console being accessed - DESKTOP ONLY with higher threshold
        () => {
          if (isMobile()) return false; // Skip on mobile
          const threshold = 300; // Higher threshold to prevent false positives
          const start = performance.now();
          debugger;
          const end = performance.now();
          return (end - start) > threshold;
        }
      ];

      const isDevToolsOpen = devToolsChecks.some(check => {
        try {
          return check();
        } catch {
          return false;
        }
      });

      if (isDevToolsOpen) {
        setIsSecure(false);
        blockAccess();
      }
    };

    const blockAccess = () => {
      // Replace page content with security message
      document.body.innerHTML = `
        <div style="
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background: 
            radial-gradient(circle at 20% 80%, rgba(0, 255, 255, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255, 20, 147, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(255, 165, 0, 0.1) 0%, transparent 50%),
            linear-gradient(45deg, rgba(0, 255, 255, 0.05) 0%, rgba(255, 20, 147, 0.05) 100%),
            #000000;
          background-size: 100% 100%, 100% 100%, 100% 100%, 100% 100%;
          animation: gridPulse 4s ease-in-out infinite;
          display: flex;
          justify-content: center;
          align-items: center;
          font-family: monospace;
          color: white;
          z-index: 999999;
        ">
          <style>
            @keyframes gridPulse {
              0%, 100% { opacity: 0.8; }
              50% { opacity: 1; }
            }
            @keyframes neonPulse {
              0%, 100% { box-shadow: 0 0 20px rgba(0, 255, 255, 0.3), inset 0 0 20px rgba(0, 255, 255, 0.1); }
              50% { box-shadow: 0 0 40px rgba(0, 255, 255, 0.5), inset 0 0 30px rgba(0, 255, 255, 0.2); }
            }
            @keyframes glitch {
              0%, 100% { transform: translateX(0px); filter: hue-rotate(0deg); }
              10% { transform: translateX(-2px); filter: hue-rotate(90deg); }
              20% { transform: translateX(2px); filter: hue-rotate(180deg); }
              30% { transform: translateX(-1px); filter: hue-rotate(270deg); }
              40% { transform: translateX(1px); filter: hue-rotate(0deg); }
              50% { transform: translateX(0px); filter: hue-rotate(90deg); }
            }
          </style>
          <div style="
            text-align: center;
            max-width: 500px;
            padding: 32px;
            background: rgba(0, 0, 0, 0.9);
            border: 2px solid rgba(0, 255, 255, 0.3);
            border-radius: 8px;
            animation: neonPulse 2s ease-in-out infinite;
            backdrop-filter: blur(10px);
            position: relative;
          ">
            <div style="font-size: 48px; margin-bottom: 24px; animation: glitch 3s ease-in-out infinite;">⚠️</div>
            <h1 style="
              margin: 0 0 24px 0; 
              font-size: 24px; 
              font-weight: 600;
              background: linear-gradient(45deg, #00ffff, #ff1493);
              background-clip: text;
              -webkit-background-clip: text;
              -webkit-text-fill-color: transparent;
              text-transform: uppercase;
              letter-spacing: 2px;
            ">
              SECURITY BREACH DETECTED
            </h1>
            <p style="
              margin: 0 0 24px 0; 
              font-size: 14px; 
              line-height: 1.6; 
              color: rgba(255, 255, 255, 0.8);
              font-family: monospace;
            ">
              > UNAUTHORIZED DEBUGGING TOOLS DETECTED<br>
              > NEURAL SECURITY PROTOCOL ACTIVATED<br>
              > SYSTEM ACCESS TERMINATED
            </p>
            <div style="
              padding: 16px;
              background: rgba(0, 255, 255, 0.1);
              border: 1px solid rgba(0, 255, 255, 0.3);
              border-radius: 4px;
              font-size: 12px;
              color: #00ffff;
              font-family: monospace;
              text-transform: uppercase;
              letter-spacing: 1px;
            ">
              [ACTION_REQUIRED] CLOSE DEVTOOLS & REFRESH
            </div>
            
            <!-- Decorative elements -->
            <div style="
              position: absolute;
              top: -2px;
              left: -2px;
              right: -2px;
              bottom: -2px;
              background: linear-gradient(45deg, #00ffff, #ff1493, #ffa500, #00ffff);
              border-radius: 8px;
              z-index: -1;
              opacity: 0.3;
              animation: glitch 4s ease-in-out infinite;
            "></div>
          </div>
        </div>
      `;

      // Don't throw error - just set state and block
      // throw new Error('Security violation detected');
    };

    // Anti-debugging techniques - balanced approach (DESKTOP ONLY)
    const antiDebug = () => {
      if (isMobile()) return; // Skip anti-debug on mobile
      
      // Multiple detection methods for better accuracy
      let detectionCount = 0;
      
      const checkDevToolsMultiple = () => {
        if (!isSecure) return; // Don't check if already blocked
        
        // Method 1: Timing-based detection - higher threshold for stability
        const start = performance.now();
        debugger;
        const end = performance.now();
        
        if (end - start > 250) { // Higher threshold
          detectionCount++;
        }
        
        // Method 2: Console override detection - less frequent checks
        const consoleCheck = () => {
          let devtools = false;
          const threshold = 400; // Much higher threshold
          setInterval(() => {
            const start = performance.now();
            debugger;
            if (performance.now() - start > threshold) {
              devtools = true;
              detectionCount++;
            }
          }, 2000); // Less frequent checks
          return devtools;
        };
        
        // Trigger block if multiple detections - higher threshold
        if (detectionCount >= 3) { // Require more detections before blocking
          blockAccess();
        }
      };
      
      // Start checks after longer delay and less frequent
      setTimeout(() => {
        setInterval(checkDevToolsMultiple, 3000); // Much less frequent
      }, 5000); // Longer initial delay
    };

    // Disable common debugging shortcuts
    const preventDebugging = (e) => {
      // List of disabled key combinations
      const disabledKeys = [
        { key: 'F12' },
        { key: 'F11' },
        { key: 'I', ctrl: true, shift: true },
        { key: 'J', ctrl: true, shift: true },
        { key: 'C', ctrl: true, shift: true },
        { key: 'K', ctrl: true, shift: true },
        { key: 'U', ctrl: true },
        { key: 'S', ctrl: true },
        { key: 'A', ctrl: true },
        { key: 'P', ctrl: true },
        { key: 'F', ctrl: true },
        { key: 'H', ctrl: true },
        { key: 'R', ctrl: true },
        { key: 'Enter', alt: true },
      ];

      const isDisabled = disabledKeys.some(combo => {
        return e.key === combo.key && 
               (!combo.ctrl || e.ctrlKey) && 
               (!combo.shift || e.shiftKey) && 
               (!combo.alt || e.altKey);
      });

      if (isDisabled) {
        e.preventDefault();
        e.stopPropagation();
        // Only block access for the most serious violations (F12, DevTools shortcuts)
        if (e.key === 'F12' || 
            (e.key === 'I' && e.ctrlKey && e.shiftKey) ||
            (e.key === 'J' && e.ctrlKey && e.shiftKey)) {
          blockAccess();
        }
        return false;
      }
    };

    // Disable right-click context menu - but don't block access immediately
    const preventRightClick = (e) => {
      e.preventDefault();
      // Just prevent right click, don't block the entire app
      return false;
    };

    // Disable text selection and copying
    const disableSelection = () => {
      document.body.style.userSelect = 'none';
      document.body.style.webkitUserSelect = 'none';
      document.body.style.mozUserSelect = 'none';
      document.body.style.msUserSelect = 'none';
      document.body.style.webkitTouchCallout = 'none';
      document.body.style.webkitUserDrag = 'none';
      document.body.style.khtmlUserSelect = 'none';
      document.onselectstart = () => false;
      document.ondragstart = () => false;
    };

    // Monitor for suspicious activity
    const monitorActivity = () => {
      // Check for common automation tools
      if (window.navigator.webdriver ||
          window.domAutomation ||
          window.domAutomationController ||
          window.webdriver ||
          window.fxdriver_id ||
          window.FXDriver ||
          window.callSelenium ||
          window.callPhantom) {
        blockAccess();
      }

      // Check for headless browsers
      if (navigator.userAgent.includes('HeadlessChrome') ||
          navigator.userAgent.includes('PhantomJS') ||
          navigator.userAgent.includes('SlimerJS')) {
        blockAccess();
      }
    };

    // Override console methods
    const protectConsole = () => {
      const noop = () => {};
      ['log', 'debug', 'info', 'warn', 'error', 'assert', 'dir', 'dirxml',
       'group', 'groupEnd', 'time', 'timeEnd', 'count', 'trace', 'profile',
       'profileEnd', 'clear', 'table'].forEach(method => {
        console[method] = noop;
      });
    };

    // Initialize security measures with proper timing
    // IMMEDIATE check for pre-opened DevTools - but don't crash
    try {
      const isDevToolsAlreadyOpen = immediateDevToolsCheck();
      
      if (isDevToolsAlreadyOpen) {
        return; // Stop here if DevTools already detected
      }
    } catch (error) {
      console.log('Security check error (non-critical):', error);
    }

    // Start basic protection immediately
    disableSelection();
    
    // Start advanced checks after page loads
    setTimeout(() => {
      try {
        performSecurityChecks();
        monitorActivity();
        antiDebug();
        
        // Set up periodic checks
        setInterval(() => {
          try {
            performSecurityChecks();
          } catch (error) {
            console.log('Security check error:', error);
          }
        }, 2000);
      } catch (error) {
        console.log('Security initialization error:', error);
      }
    }, 2000); // Wait 2 seconds for page to stabilize
    
    // Protect console after initial delay
    setTimeout(() => {
      protectConsole();
    }, 1000);

    // Add event listeners
    document.addEventListener('keydown', preventDebugging, true);
    document.addEventListener('keyup', preventDebugging, true);
    document.addEventListener('keypress', preventDebugging, true);
    document.addEventListener('contextmenu', preventRightClick, true);
    
    // Listen for resize events (DevTools opening/closing changes window size)
    const handleResize = () => {
      if (!isMobile()) {
        setTimeout(() => {
          try {
            const widthDiff = window.outerWidth - window.innerWidth;
            const heightDiff = window.outerHeight - window.innerHeight;
            
            if ((widthDiff > 200 || heightDiff > 200) && !document.fullscreenElement) {
              console.log('DevTools detected via window resize');
              setIsSecure(false);
              blockAccess();
            }
          } catch (error) {
            console.log('Resize check error:', error);
          }
        }, 500); // Small delay to ensure resize is complete
      }
    };
    
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => {
      document.removeEventListener('keydown', preventDebugging, true);
      document.removeEventListener('keyup', preventDebugging, true);
      document.removeEventListener('keypress', preventDebugging, true);
      document.removeEventListener('contextmenu', preventRightClick, true);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // If security is compromised, don't render the app
  if (!isSecure) {
    return null;
  }

  return <>{children}</>;
};

export default SecurityWrapper; 